import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Table,
  Stack,
  Button,
  Tooltip,
  Divider,
  TextField,
  TableBody,
  Container,
  IconButton,
  InputAdornment,
  TableContainer,
  TablePagination,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useSettings from '../../../hooks/useSettings';
import useTable, { getComparator } from '../../../hooks/useTable';
import useColumnVisibility, { ColumnDef } from '../../../hooks/useColumnVisibility';
// components
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import {
  TableNoData,
  ColumnsButton,
  TableSkeleton,
  TableHeadCustom,
  TableSelectedActions,
} from '../../../components/table';
// data + sections
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { apiErrorText } from 'src/utils/apiErrorText';
import { Diploma } from 'src/@types/Diploma';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import { DiplomaModal } from './DiplomaModal';
import DiplomaTableRow from './DiplomaTableRow';
import useLandingSummaries from './landing/useLandingSummaries';

// ----------------------------------------------------------------------

// Description and Enrolled start hidden so Landing, Status and the row menu
// (the way into "Landing page") fit a 1080 px card without scrolling.
const DIPLOMA_COLUMNS: ColumnDef[] = [
  { id: 'title', label: 'Diploma', align: 'left' },
  { id: 'description', label: 'Description', align: 'left', defaultHidden: true },
  { id: 'price', label: 'Price', align: 'left' },
  { id: 'duration', label: 'Duration', align: 'center' },
  { id: 'batches', label: 'Batches', align: 'left' },
  { id: 'batchCount', label: 'Enrolled', align: 'center', defaultHidden: true },
  { id: 'landing', label: 'Landing', align: 'left' },
  { id: 'status', label: 'Status', align: 'center' },
];
// v2: the Landing column and the new defaults apply once to every browser.
const DIPLOMA_COLUMNS_STORAGE_KEY = 'kayfa.diplomas.visibleColumns.v2';

// ----------------------------------------------------------------------

export function DiplomaList() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    onSort,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({ defaultOrderBy: 'createdAt', defaultOrder: 'desc' });

  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  const [tableData, setTableData] = useState<Diploma[]>([]);
  const [countMap, setCountMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [currentDiploma, setCurrentDiploma] = useState<Diploma | undefined>();
  const [deleteState, setDeleteState] = useState<{ open: boolean; id?: string }>({
    open: false,
  });

  const {
    visibleColumns,
    toggleColumn,
    headLabel: diplomaHeadLabel,
  } = useColumnVisibility(DIPLOMA_COLUMNS, DIPLOMA_COLUMNS_STORAGE_KEY);

  // Landing column: one extra request on mount (status, x/19, new leads).
  const landing = useLandingSummaries();

  const getDiplomas = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIS.DIPLOMAS.LIST);
      const list = res.data?.data ?? res.data;
      setTableData(Array.isArray(list) ? list : []);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load diplomas', { variant: 'error' });
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  // Enrolled student count per batch, keyed `${diplomaId}|${batchName}`.
  const getEnrollmentCounts = async () => {
    try {
      const res = await axiosInstance.get(APIS.DIPLOMAS.ENROLLMENT_COUNTS);
      const list = res.data?.data ?? res.data ?? [];
      const map: Record<string, number> = {};
      (Array.isArray(list) ? list : []).forEach((r: any) => {
        map[`${r.diploma}|${r.batchName}`] = r.count;
      });
      setCountMap(map);
    } catch {
      setCountMap({});
    }
  };

  useEffect(() => {
    getDiplomas().catch(console.error);
    getEnrollmentCounts().catch(console.error);
  }, []);

  // Edit opens on a FRESH read of the diploma, never the list row (loaded once
  // per visit): a row saved back would overwrite someone else's later change
  // (R5 D2). A failed read opens nothing. The last click wins.
  const editSeq = useRef(0);
  const handleEditRow = async (diploma: Diploma) => {
    if (!diploma?._id) return;
    const seq = ++editSeq.current;
    try {
      const res = await axiosInstance.get(APIS.DIPLOMAS.BY_ID(diploma._id));
      const fresh = res.data?.data ?? res.data;
      if (!fresh || !fresh._id) throw new Error('Diploma not found');
      if (seq !== editSeq.current) return;
      setCurrentDiploma(fresh);
      setModalOpen(true);
    } catch (err: any) {
      if (seq !== editSeq.current) return;
      enqueueSnackbar(apiErrorText(err, 'Could not open the diploma'), { variant: 'error' });
    }
  };

  const handleDeleteDiploma = async () => {
    if (!deleteState.id) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(APIS.DIPLOMAS.DELETE(deleteState.id));
      enqueueSnackbar('Diploma deleted');
      setDeleteState({ open: false });
      await getDiplomas();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to delete diploma', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const dataFiltered = applySortFilter({
    tableData,
    comparator: getComparator(order, orderBy),
    filterName,
  });

  const isNotFound = !dataFiltered.length && !!filterName;
  const tableHead = [...diplomaHeadLabel, { id: '' }];

  return (
    <Page title="Diplomas: List">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Diplomas"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Diplomas', href: PATH_DASHBOARD.diplomas },
          ]}
          action={
            <Button
              variant="contained"
              onClick={() => {
                setCurrentDiploma(undefined);
                setModalOpen(true);
              }}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              New Diploma
            </Button>
          }
        />

        <Card>
          <Divider />

          <Stack
            direction="row"
            alignItems="center"
            sx={{ p: 2, gap: 1.5, flexWrap: 'wrap' }}
          >
            <TextField
              size="small"
              value={filterName}
              onChange={(e) => {
                setFilterName(e.target.value);
                setPage(0);
              }}
              placeholder="Search diploma..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 240 }}
            />
            <ColumnsButton
              columns={DIPLOMA_COLUMNS}
              visibleColumns={visibleColumns}
              onToggle={toggleColumn}
            />
          </Stack>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
              {selected.length > 0 && (
                <TableSelectedActions
                  dense={dense}
                  numSelected={selected.length}
                  rowCount={tableData.length}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      tableData.map((row) => row._id as string)
                    )
                  }
                  actions={
                    <Tooltip title="Clear">
                      <IconButton color="primary" onClick={() => setSelected([])}>
                        <Iconify icon={'eva:close-fill'} />
                      </IconButton>
                    </Tooltip>
                  }
                />
              )}

              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={tableHead}
                  rowCount={tableData.length}
                  numSelected={selected.length}
                  onSort={onSort}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      tableData.map((row) => row._id as string)
                    )
                  }
                />

                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => <TableSkeleton key={`d-skeleton-${i}`} />)
                  ) : (
                    <>
                      {dataFiltered
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row) => (
                          <DiplomaTableRow
                            key={row._id}
                            row={row}
                            visibleColumns={visibleColumns}
                            countMap={countMap}
                            landingSummary={landing.byId[row._id as string]}
                            landingLoaded={landing.loaded}
                            selected={selected.includes(row._id as string)}
                            onSelectRow={() => onSelectRow(row._id as string)}
                            onEditRow={() => handleEditRow(row)}
                            onDeleteRow={() =>
                              setDeleteState({ open: true, id: row._id as string })
                            }
                          />
                        ))}
                      <TableNoData isNotFound={isNotFound} />
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={dataFiltered.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />
          </Box>
        </Card>
      </Container>

      <DiplomaModal
        isEdit={currentDiploma ? true : false}
        currentDiploma={currentDiploma}
        open={modalOpen}
        handleClose={(saved?: boolean) => {
          setModalOpen(false);
          setCurrentDiploma(undefined);
          if (saved) {
            getDiplomas();
            landing.reload();
          }
        }}
      />

      <ConfirmationDialog
        isOpen={deleteState.open}
        onClose={() => setDeleteState({ open: false })}
        confirmAction={handleDeleteDiploma}
        headText={'Delete Diploma'}
        text={'Are you sure you want to delete this diploma (and all its batches)?'}
        loading={deleting}
      />
    </Page>
  );
}

// ----------------------------------------------------------------------

function applySortFilter({
  tableData,
  comparator,
  filterName,
}: {
  tableData: Diploma[];
  comparator: (a: any, b: any) => number;
  filterName: string;
}) {
  const stabilized = tableData.map((el, index) => [el, index] as const);
  stabilized.sort((a, b) => {
    const o = comparator(a[0], b[0]);
    if (o !== 0) return o;
    return a[1] - b[1];
  });
  let rows = stabilized.map((el) => el[0]);
  if (filterName) {
    rows = rows.filter(
      (item) => (item.title || '').toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }
  return rows;
}
